import React, { useState, useEffect } from 'react';

import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Descriptions,
  Modal,
  Tag,
  Select
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

export default function ShelfTypeShow({
  shelfType: initialShelfType,
  shelfTypeService,
  role = 'operator',
  basePath = '/shelf_types'
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shelfType, setShelfType] = useState(initialShelfType);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isMigrateModalVisible, setIsMigrateModalVisible] = useState(false);
  const [replacementId, setReplacementId] = useState(null);
  const [shelfTypeOptions, setShelfTypeOptions] = useState([]);

  useEffect(() => {
    if (initialShelfType) {
      setShelfType(initialShelfType);
      form.setFieldsValue(initialShelfType);
    }
  }, [initialShelfType, form]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.setFieldsValue(shelfType);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const response = await shelfTypeService.update(shelfType.id, values);
      if (response.success) {
        message.success(t('common.message.update_success'));
        setShelfType(response.data);
        setIsEditMode(false);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.update_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Update shelf type error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (shelfType.deletable === false) {
      openMigrateModal();
    } else {
      setIsConfirmModalVisible(true);
    }
  };

  const performDelete = async () => {
    setDeleting(true);
    try {
      const response = await shelfTypeService.delete(shelfType.id);
      if (response.success) {
        message.success(t('common.message.delete_success'));
        setIsConfirmModalVisible(false);
        navigate(basePath);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Delete shelf type error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const openMigrateModal = async () => {
    setReplacementId(null);
    setIsMigrateModalVisible(true);
    try {
      const response = await shelfTypeService.fetchList();
      if (response.success) {
        setShelfTypeOptions(
          response.data
            .filter((st) => st.id !== shelfType.id)
            .map((st) => ({ label: st.name, value: st.id }))
        );
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load shelf types error:', error);
    }
  };

  const handleMigrateDelete = async () => {
    if (!replacementId) {
      message.warning(t('shared.shelf_types.delete_modal.replacement_required'));
      return;
    }
    setDeleting(true);
    try {
      const response = await shelfTypeService.delete(shelfType.id, replacementId);
      if (response.success) {
        message.success(t('common.message.delete_success'));
        setIsMigrateModalVisible(false);
        navigate(basePath);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Delete shelf type error:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (!shelfType) {
    return (
      <Card>
        <p>{t('shared.shelf_types.detail.not_found')}</p>
      </Card>
    );
  }

  const isCodeEditable = shelfType.code_editable !== false;
  const showDeleteButton = !shelfType.system_reserved;
  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  const headerActions = !isEditMode && (
    <Space>
      <Button
        type="primary"
        icon={<EditOutlined />}
        onClick={handleEdit}
      >
        {t('common.button.edit')}
      </Button>
      {showDeleteButton && (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={handleDeleteClick}
          loading={deleting}
        >
          {t('common.button.delete')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['warehouse_settings', 'warehouse_settings.shelf_types', { label: shelfType.name }]}
        actions={headerActions}
        title={
          <Space>
            {shelfType.name}
            {shelfType.system_reserved && (
              <Tag color="blue">{t('shared.shelf_types.detail.system_reserved_badge')}</Tag>
            )}
          </Space>
        }
      />
      <Card>
        {isEditMode ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={shelfType}
          >
            <Form.Item
              label={t('models.shelf_type.name')}
              name="name"
              rules={[
                { required: true, message: t('common.form.required') }
              ]}
            >
              <Input placeholder={t('shared.shelf_types.form.name_placeholder')} />
            </Form.Item>

            <Form.Item
              label={t('models.shelf_type.code')}
              name="code"
              extra={!isCodeEditable ? t('shared.shelf_types.detail.system_reserved_code_hint') : undefined}
            >
              <Input
                placeholder={t('shared.shelf_types.form.code_placeholder')}
                disabled={!isCodeEditable}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={saving}
                >
                  {t('common.button.save')}
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  {t('common.button.cancel')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('models.shelf_type.name')}>
              {shelfType.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.shelf_type.code')}>
              {shelfType.code ? <code>{shelfType.code}</code> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.shelf_type.system_reserved')}>
              {shelfType.system_reserved
                ? <Tag color="blue">{t('shared.shelf_types.detail.system_reserved_badge')}</Tag>
                : '-'}
            </Descriptions.Item>
            {shelfType.system_reserved && shelfType.code && (
              <Descriptions.Item label={t('shared.shelf_types.detail.description')}>
                {t(`shared.shelf_types.system_codes.${shelfType.code}`, { defaultValue: '-' })}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('models.shelf_type.shelves_count')}>
              {shelfType.shelves_count ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.shelf_type.merchant_shelf_types_count')}>
              {shelfType.merchant_shelf_types_count ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {shelfType.created_at ? formatDateTime(shelfType.created_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.updated_at')}>
              {shelfType.updated_at ? formatDateTime(shelfType.updated_at) : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Modal
        title={t('shared.shelf_types.detail.delete_confirm')}
        open={isConfirmModalVisible}
        onOk={performDelete}
        onCancel={() => setIsConfirmModalVisible(false)}
        confirmLoading={deleting}
        okText={t('common.button.confirm')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.button.cancel')}
      >
        <p>{t('shared.shelf_types.detail.delete_confirm_description')}</p>
      </Modal>

      <Modal
        title={t('shared.shelf_types.delete_modal.title')}
        open={isMigrateModalVisible}
        onOk={handleMigrateDelete}
        onCancel={() => setIsMigrateModalVisible(false)}
        confirmLoading={deleting}
        okText={t('common.button.confirm_delete')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <p>
            {t('shared.shelf_types.delete_modal.description', { name: shelfType.name })}
          </p>
          <Form layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              label={t('shared.shelf_types.delete_modal.replacement_label')}
              required
              help={t('shared.shelf_types.delete_modal.replacement_help')}
            >
              <Select
                placeholder={t('shared.shelf_types.delete_modal.replacement_placeholder')}
                onChange={setReplacementId}
                value={replacementId}
                options={shelfTypeOptions}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
}
